import { useState, useEffect, useCallback, useMemo } from 'react';
import { cloneDeep, isEqual } from 'lodash';
import { useMutation } from '@apollo/client';
import { DateTime } from 'luxon';

import type {
  App,
  OperatingHours,
  Venue,
  UpdateOperatingHoursMutation,
  UpdateOperatingHoursMutationVariables
} from 'generated/graphql';

import { OperatingHoursInput, StatusEnum } from 'generated/graphql';

import { UPDATE_OPERATING_HOURS_MUTATION, VENUE_FRAGMENT } from 'queries/Venue';

import { useAppContext } from 'AppContext';

import type { MenuHoursType, DayOfWeekType, AllMenuHours, DayOfWeekKey, ClosedDates } from 'types';
import { convertDayOfWeekKeyToDayOfWeekEnum } from 'types';

import { DAYS_OF_WEEK, DEFAULT_TIMEZONE, END_OF_DAY, START_OF_DAY } from 'consts';

import { convertTo12HourFormat } from 'utils/dateTimeHelpers';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import { UNCATEGORISED_CATEGORY_MMID } from './constants';

const {
  MENULOG_ID,
  SKIP_ID,
  OPERATING_HOURS_WHITELISTED_APP_IDS,
  ENABLE_OPERATING_HOURS_FOR_VIRTUAL_BRAND_VENUES,
  ENABLE_OPERATING_HOURS_FOR_VENUES_WITH_SKIP,
  CLOSED_DATES_WHITELISTED_APP_IDS
} = getEnvironmentVariables();

const DAYS_IN_WEEK = 7;
const SHIFT_DAYS_LEFT = 6;
const VENUE_KEY = 'venue';

const SUBMISSION_STATUSES = ['IDLE', 'PENDING', 'FAILURE', 'SUCCESS'] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

/**
 * Transform the menu-manager MMHours back to Doshii OperatingHours format
 */
export const transformToDoshiiHours = (allMMHours: AllMenuHours): OperatingHours[] => {
  const newOperatingHours: OperatingHours[] = [];

  Object.entries(allMMHours).forEach(([key, menuMMHours]) => {
    const isVenue = key === VENUE_KEY;
    let appId: number | null = null;
    if (!isVenue) {
      if (key.includes('_')) {
        appId = Number(key.split('_')[0]);
      } else {
        appId = Number(key);
      }
    }
    const menuId = key.includes('_') ? key.split('_')[1] : null;
    const processedDays = new Set<string>();
    Object.values(menuMMHours).forEach(({ day, hours }) => {
      if (hours.status === StatusEnum.Open) {
        hours.time.forEach(({ from, to }) => {
          if (DAYS_OF_WEEK.includes(day)) {
            newOperatingHours.push({
              daysOfWeek: [convertDayOfWeekKeyToDayOfWeekEnum(day)],
              status: StatusEnum.Open,
              time: { from, to },
              ...(appId && { appId }),
              ...(menuId && { menuId })
            });
            processedDays.add(day);
          }
        });
      }
    });

    DAYS_OF_WEEK.forEach((day) => {
      if (!processedDays.has(day)) {
        newOperatingHours.push({
          daysOfWeek: [convertDayOfWeekKeyToDayOfWeekEnum(day)],
          status: StatusEnum.Closed,
          ...(appId && { appId }),
          ...(menuId && { menuId })
        });
      }
    });
  });

  return newOperatingHours;
};

/**
 * Transform the Doshii OperatingHours to menu-manager friendly menuMMHours format.
 */
export const transformToMMHours = (operatingHours: OperatingHours[]): AllMenuHours => {
  return operatingHours.reduce<AllMenuHours>((acc, record) => {
    const { dates, time, appId, menuId, daysOfWeek } = record;
    let key: string;
    if (appId) {
      key = menuId ? `${appId}_${menuId}` : `${appId}`;
    } else {
      key = VENUE_KEY;
    }

    if (dates) {
      // Handle special hours with dates future ticket
    } else {
      acc[key] = acc[key] || {};

      // Ensure all days of week are initialised
      DAYS_OF_WEEK.forEach((day) => {
        if (!acc[key][day]) {
          acc[key][day] = { day, hours: { status: StatusEnum.Closed, time: [] } };
        }
      });

      // Populate days with provided hours
      daysOfWeek?.forEach((day) => {
        if (time) {
          acc[key][day].hours.status = StatusEnum.Open;
          acc[key][day].hours.time.push({ from: time.from, to: time.to });
        }
      });
    }

    return acc;
  }, {});
};

const initialiseClosedMenuHours = (): MenuHoursType => {
  const createClosedHours = (): DayOfWeekType['hours'] => ({
    status: StatusEnum.Closed,
    time: [
      {
        from: START_OF_DAY,
        to: END_OF_DAY
      }
    ]
  });

  return {
    mon: { day: 'mon', hours: createClosedHours() },
    tue: { day: 'tue', hours: createClosedHours() },
    wed: { day: 'wed', hours: createClosedHours() },
    thu: { day: 'thu', hours: createClosedHours() },
    fri: { day: 'fri', hours: createClosedHours() },
    sat: { day: 'sat', hours: createClosedHours() },
    sun: { day: 'sun', hours: createClosedHours() }
  };
};

/**
 * Determine common main menu hours for the venue.
 *
 * Returns `null` if any of the following are true:
 * - The venue uses a virtual brand
 * - At least one app uses a timed menu (doshii:menu)
 * - There are multiple primary menus with distinct hours
 *
 * Returns the common menu hours if any of the following are true:
 * - There is only one primary menu
 * - All primary menus have identical hours
 * - There is no primary menu, but venue-level hours are available
 */
export const getCommonHoursAcrossAllMenus = (currentVenue: Venue, allMMHours: AllMenuHours): MenuHoursType | null => {
  const hasUnsupportedMenu = currentVenue.menus?.some(
    (menu) =>
      menu.apps?.some((app) => app.id.startsWith('virtual.')) ||
      menu.categories?.some((category) => category.category.startsWith('doshii:menu'))
  );

  if (hasUnsupportedMenu) {
    return null;
  }

  // Extract primary menu hours and check menu equality
  const { primaryMenus, allEqual } = Object.entries(allMMHours).reduce<{
    primaryMenus: MenuHoursType[];
    allEqual: boolean;
  }>(
    (acc, [key, value]) => {
      const isTimedMenu = key.includes('_');
      const isVenue = key === VENUE_KEY;

      if (!isTimedMenu && !isVenue) {
        if (acc.primaryMenus.length === 0) {
          acc.primaryMenus.push(value);
        } else {
          if (acc.allEqual && !isEqual(acc.primaryMenus[0], value)) {
            acc.allEqual = false;
          }
          acc.primaryMenus.push(value);
        }
      }
      return acc;
    },
    { primaryMenus: [], allEqual: true }
  );

  // If there are no primary menu hours available, return the venue hours or initialise closed hours
  if (primaryMenus.length === 0) {
    return allMMHours.venue || initialiseClosedMenuHours();
  }

  // If there is only one primary menu, that's our default
  if (primaryMenus.length === 1) {
    return primaryMenus[0];
  }

  // For multiple primary menus, return the common hours only if all are equal
  return allEqual ? primaryMenus[0] : null;
};
/**
 * Initialises main or timed menu(doshiiMenu) for the given appId and doshiiMenu.
 */
export const initialiseMenuHours = (
  commonHours: MenuHoursType | null,
  allMenuHours: AllMenuHours,
  appId: string,
  doshiiMenu?: string
): { initialisedMenuHours: AllMenuHours; isCommonHoursApplied: boolean } => {
  const clonedAllMenuHours = cloneDeep(allMenuHours);
  const menuKey = doshiiMenu ? `${appId}_${doshiiMenu}` : appId;
  let isCommonHoursApplied = false;

  /* Different initialisation strategies for main menus and timed menus */
  if (!doshiiMenu) {
    // Main menu
    // Use default hours if available
    if (commonHours) {
      clonedAllMenuHours[menuKey] = cloneDeep(commonHours);
      isCommonHoursApplied = true;
    }
    // Fall back to venue hours if available
    else if (allMenuHours?.venue) {
      clonedAllMenuHours[menuKey] = cloneDeep(allMenuHours.venue);
    }
    // As a last resort initialise as closed for all days
    else {
      clonedAllMenuHours[menuKey] = initialiseClosedMenuHours();
    }
  } else {
    // Timed menu
    // Always start as closed for all days
    clonedAllMenuHours[menuKey] = initialiseClosedMenuHours();
  }

  return { initialisedMenuHours: clonedAllMenuHours, isCommonHoursApplied };
};

const isDayOfWeekKey = (value: string): value is DayOfWeekKey => {
  return ([...DAYS_OF_WEEK] as string[]).indexOf(value) > -1;
};

/**
 * Convert time string (HH:MM) to minutes since midnight
 */
const convertTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Validate operating hours to ensure time ranges are at least 30 minutes long
 * and that at least one day of the week is open
 */
export const findInvalidHours = (inputMenuHours: MenuHoursType): MenuHoursType => {
  const validatedHours = cloneDeep(inputMenuHours);
  let allDaysClosed = true;

  Object.values(validatedHours).forEach((dayRecord) => {
    // Clear any existing errors
    delete dayRecord.error;

    if (dayRecord.hours.status === StatusEnum.Open) {
      allDaysClosed = false;

      dayRecord.hours.time.some((timeRange) => {
        const fromMinutes = convertTimeToMinutes(timeRange.from);
        const toMinutes = convertTimeToMinutes(timeRange.to);

        if (toMinutes - fromMinutes < 30) {
          dayRecord.error = 'Each open time range must be at least 30 minutes long.';
          return true;
        }
        return false;
      });
    }
  });

  if (allDaysClosed) {
    validatedHours.sun.error = 'At least one day of the week must be open.';
  }
  return validatedHours;
};

/**
 * Get active menu keys for a specific app in a venue
 */
export const getActiveMenusForApp = (currentAppId: string, currentVenue?: Venue): string[] => {
  const activeMenus = new Set<string>();

  currentVenue?.menus?.forEach((menu) => {
    if (menu.apps[0].id === currentAppId) {
      menu.categories?.forEach((category) => {
        if (category.category.startsWith('doshii:menu:')) {
          const doshiiMenuName = category.category.split(':')[2];
          // Timed menu
          activeMenus.add(`${currentAppId}_${doshiiMenuName}`);
        } else if (category.mmId !== UNCATEGORISED_CATEGORY_MMID) {
          // Main menu
          activeMenus.add(currentAppId);
        }
      });
    }
  });

  return Array.from(activeMenus);
};

/**
 * Find overlapping hours between the current menu and other menus for the same app,
 * if found add a message to each affected day in current menu
 */
export const findOverlappingTimes = (
  currentMenuKey: string,
  currentMenuMMHours: MenuHoursType,
  allMMHours?: AllMenuHours | null,
  activeMenus?: string[]
): MenuHoursType => {
  if (!allMMHours) return currentMenuMMHours;
  const appId = currentMenuKey.split('_')[0];

  const otherMenusOfSameApp: AllMenuHours = {};

  Object.entries(allMMHours).forEach(([menuKey, menuValue]) => {
    if (menuKey.startsWith(appId) && menuKey !== currentMenuKey && (!activeMenus || activeMenus.includes(menuKey))) {
      otherMenusOfSameApp[menuKey] = menuValue;
    }
  });

  Object.entries(currentMenuMMHours).forEach(([day, dayRecords]) => {
    if (!isDayOfWeekKey(day)) {
      throw new Error(`Expected day of week key but got ${day}`);
    }
    if (dayRecords.hours.status === 'closed') return;

    const currentTimes = dayRecords.hours.time;

    Object.entries(otherMenusOfSameApp).forEach(([menuKey, menuHours]) => {
      const otherMenuHours = menuHours[day];

      if (otherMenuHours.hours.status === 'closed') return;

      const otherMenuTimes = otherMenuHours.hours.time;

      // Check for overlapping time ranges between the current menu and each of the other menu
      currentTimes.forEach(({ from: from1, to: to1 }) => {
        otherMenuTimes.forEach(({ from: from2, to: to2 }) => {
          if (from1 <= to2 && from2 <= to1) {
            let menuName = 'Main';
            if (menuKey.includes('_') && menuKey.startsWith(`${appId}_`)) {
              menuName = menuKey.slice(menuKey.indexOf('_') + 1);
            }
            dayRecords.error = `Your selection overlaps with ${menuName} menu which is open from ${otherMenuTimes
              .map((time) => `${convertTo12HourFormat(time.from)} to ${convertTo12HourFormat(time.to)}`)
              .join(', ')}. Choose a different time.`;
          }
        });
      });
    });
  });
  return currentMenuMMHours;
};

/**
 * Transform YYYY-MM-DD date string to Doshii operating hours date format { day, month, year }
 */
const formatDate = (date: string): { day: number; month: number; year: number } => {
  const [year, month, day] = date.split('-').map(Number);
  return { day, month, year };
};

/**
 * Transform the mm special hours with dates to Doshii OperatingHours format
 * Currently only supports closed dates
 */
export const transformToDoshiiSpecialHours = (specialHours: ClosedDates[]): OperatingHours[] => {
  return specialHours.reduce<OperatingHours[]>((acc, { from, to, isRange }) => {
    const dates = isRange && to ? [formatDate(from), formatDate(to)] : [formatDate(from)];

    acc.push({ dates, status: StatusEnum.Closed });

    return acc;
  }, []);
};

/**
 * These are special hours not handled by menu manager, these are sanitized and passed back on submission
 */
export const sanitizeDoshiiSpecialHoursForInput = (specialHours: OperatingHours[]): OperatingHours[] => {
  return specialHours.reduce<OperatingHours[]>((acc, { dates = [], status, time, appId, menuId }) => {
    const filteredDates = dates?.reduce<{ day: number; month: number; year: number }[]>((dateAcc, date) => {
      if (date && date.day !== undefined && date.month !== undefined && date.year !== undefined) {
        dateAcc.push({ day: date.day, month: date.month, year: date.year });
      }
      return dateAcc;
    }, []);

    if (filteredDates && filteredDates.length) {
      acc.push({
        dates: filteredDates,
        status,
        ...(time && { time: { from: time.from, to: time.to } }),
        ...(appId && { appId }),
        ...(menuId && { menuId })
      });
    }
    return acc;
  }, []);
};

/**
 * Split the special hours into active closures and other special hours.
 * Active closures = the special hours with status=closed and with date(ranges) at least partially future (these are passed back, these are displayed on dates page, these can be deleted).
 * Others = the rest of the special hours (these are always passed back untouched on submission).
 */
export const splitSpecialHoursToActiveClosuresAndOthers = (
  specialHours: OperatingHours[],
  timezone?: string | null
): [OperatingHours[], OperatingHours[]] => {
  const tz = timezone || DEFAULT_TIMEZONE;
  const today = DateTime.now().setZone(tz).startOf('day');
  const [activeClosures, others] = specialHours.reduce<[OperatingHours[], OperatingHours[]]>(
    (acc, h) => {
      if (h.status === StatusEnum.Closed && !h.time && h.dates && h.dates[0]) {
        const startDate = DateTime.fromObject(
          { year: h.dates[0].year, month: h.dates[0].month, day: h.dates[0].day },
          { zone: tz }
        );

        let isActive = startDate >= today;

        if (!isActive && h.dates[1]) {
          const endDate = DateTime.fromObject(
            { year: h.dates[1].year, month: h.dates[1].month, day: h.dates[1].day },
            { zone: tz }
          );
          isActive = endDate >= today;
        }

        if (isActive) {
          // Active closures
          acc[0].push(h);
        } else {
          // Others
          acc[1].push(h);
        }
      } else {
        // Others
        acc[1].push(h);
      }
      return acc;
    },
    [[], []]
  );

  // Sort active closures by the start date earliest first
  activeClosures.sort((a, b) => {
    if (!a.dates?.[0] || !b.dates?.[0]) return 0;

    const dateA = a.dates[0];
    const dateB = b.dates[0];

    const startDateTime = DateTime.fromObject({ year: dateA.year, month: dateA.month, day: dateA.day }, { zone: tz });
    const endDateTime = DateTime.fromObject({ year: dateB.year, month: dateB.month, day: dateB.day }, { zone: tz });

    if (startDateTime < endDateTime) {
      return -1;
    }
    if (startDateTime > endDateTime) {
      return 1;
    }
    return 0;
  });

  return [activeClosures, others];
};

/**
 * Split the operating hours into special hours with dates and recurring hours with days of week
 */
export const splitOperatingHoursToSpecialAndRecurring = (
  operatingHours: OperatingHours[]
): [OperatingHours[], OperatingHours[]] => {
  return operatingHours.reduce<[OperatingHours[], OperatingHours[]]>(
    (acc, record) => {
      if (record.dates && record.dates.length && !record.daysOfWeek) {
        // Special hours with dates
        acc[0].push(record);
      }
      if (record.daysOfWeek && record.daysOfWeek.length && !record.dates) {
        // Recurring hours with days of week
        acc[1].push(record);
      }
      return acc;
    },
    [[], []]
  );
};

/**
 * Check if there are any active closures for today
 */
export const getClosuresForToday = (
  activeClosures: OperatingHours[],
  timezone?: string | null
): OperatingHours | null => {
  if (!activeClosures.length) return null;

  const tz = timezone || DEFAULT_TIMEZONE;
  const todayDate = DateTime.now().setZone(tz).startOf('day');

  return (
    activeClosures.find((closure) => {
      const { dates } = closure;

      if (!dates?.length) return false;

      // Single date closure
      if (dates.length === 1 && dates[0]) {
        const { day, month, year } = dates[0];
        // Create date in correct timezone
        const closureDate = DateTime.fromObject({ year, month, day }, { zone: tz });
        return todayDate.hasSame(closureDate, 'day');
      }

      // Date range closure
      if (dates.length === 2 && dates[0] && dates[1]) {
        const startDate = DateTime.fromObject(
          { year: dates[0].year, month: dates[0].month, day: dates[0].day },
          { zone: tz }
        );
        const endDate = DateTime.fromObject(
          { year: dates[1].year, month: dates[1].month, day: dates[1].day },
          { zone: tz }
        );

        // Check if today is within range (inclusive)
        return todayDate >= startDate && todayDate <= endDate;
      }

      return false;
    }) || null
  );
};

/**
 * Get list of subscribed operating hours supported app
 */
export const getSubscribedOperatingHoursSupportedApps = (
  operatingHoursSupportedApps: number[],
  venue?: Venue
): App[] => {
  return (
    venue?.menus?.reduce<App[]>((acc, menu) => {
      const appId = menu.apps[0].id;
      if (!appId.startsWith('virtual.') && operatingHoursSupportedApps.includes(parseInt(appId, 10))) {
        acc.push(menu.apps[0]);
      }
      return acc;
    }, []) || []
  );
};

/**
 * Check if operating hours should be supported for the given venue.
 * Both conditions must be meet:
 * 1. The currentAppId is either not provided or is in the OPERATING_HOURS_WHITELISTED_APP_IDS.
 * 2. If ENABLE_OPERATING_HOURS_FOR_VIRTUAL_BRAND_VENUES is false - the venue must not have any virtual brand
 */
export const checkOperatingHoursSupported = ({
  currentAppId,
  currentVenue,
  operatingHoursWhitelistedAppIds,
  enableOperatingHoursForVirtualBrandVenues,
  enableOperatingHoursForVenuesWithSkip
}: {
  currentAppId?: string;
  currentVenue?: Venue;
  operatingHoursWhitelistedAppIds: number[];
  enableOperatingHoursForVirtualBrandVenues: boolean;
  enableOperatingHoursForVenuesWithSkip: boolean;
}): boolean => {
  const isAppWhitelisted = !currentAppId || operatingHoursWhitelistedAppIds.includes(Number(currentAppId));

  if (!isAppWhitelisted) {
    return false;
  }

  const venueHasSkipAppConnection = currentVenue?.menus?.some((menu) =>
    menu.apps?.some((app) => Number(app.id) === SKIP_ID)
  );

  if (!enableOperatingHoursForVenuesWithSkip && venueHasSkipAppConnection) {
    return false;
  }

  const venueHasVirtualBrand = currentVenue?.menus?.some((menu) =>
    menu.apps?.some((app) => app.id.startsWith('virtual.'))
  );

  return enableOperatingHoursForVirtualBrandVenues || !venueHasVirtualBrand;
};

/**
 * Correct Doshii special hours based on user modifications.
 * TODO: Later in a future ticket:
 * This logic ensures historical data is preserved when a user deletes an active special hour.
 * - Iterate through `originalActiveClosures` and check if each entry exists in `currentActiveClosures`.
 * - If an entry is missing from `currentActiveClosures`, it has been deleted and needs adjustment.
 * - If the start date (`from`) is in the future, remove the record from the cloned `originalActiveClosures`.
 * - If the start date (`from`) is in the past, update the end date (`to`) to today's date in the cloned `originalActiveClosures`.
 * - If the entry has only one date (which must be today), remove it from the cloned `originalActiveClosures`.
 * - Return the cloned `originalActiveClosures` as the corrected special hours.
 */
export const correctDoshiiSpecialHours = (
  originalActiveClosures: OperatingHours[],
  currentActiveClosures: OperatingHours[]
): OperatingHours[] => {
  return currentActiveClosures;
};

/**
 * Calculate the next state of all menu hours based on what has changed
 */
export const calculateNextAllMMHours = ({
  allMMHoursInAppContext,
  hasSpecificMenuHourChanged,
  currentMenuMMHours,
  currentMenuKey,
  hasCommonHoursAcrossAllMenusChanged,
  hasCommonHoursAcrossAllMenusBeenApplied,
  commonHoursAcrossAllMenus,
  subscribedRecurringHoursSupportedApps
}: {
  allMMHoursInAppContext: AllMenuHours;
  hasSpecificMenuHourChanged: boolean;
  currentMenuMMHours: MenuHoursType | null;
  currentMenuKey: string;
  hasCommonHoursAcrossAllMenusChanged: boolean;
  hasCommonHoursAcrossAllMenusBeenApplied: boolean;
  commonHoursAcrossAllMenus: MenuHoursType | null;
  subscribedRecurringHoursSupportedApps: App[];
}): AllMenuHours => {
  // Remove commonHours from the payload as should not be sent in the payload
  const allMMHoursWithoutCommonHours = cloneDeep(allMMHoursInAppContext);
  delete allMMHoursWithoutCommonHours.commonHours;

  // Specific menu hours changed
  if (hasSpecificMenuHourChanged && currentMenuMMHours) {
    return {
      ...allMMHoursWithoutCommonHours,
      [currentMenuKey]: currentMenuMMHours
    };
  }

  // Common hours changed or were applied to menus
  if ((hasCommonHoursAcrossAllMenusChanged || hasCommonHoursAcrossAllMenusBeenApplied) && commonHoursAcrossAllMenus) {
    // Apply common hours to all subscribed apps
    const subscribedAppIds = subscribedRecurringHoursSupportedApps.map((app) => app.id);
    const newMainMenuHours = Object.fromEntries(subscribedAppIds.map((appId) => [appId, commonHoursAcrossAllMenus]));

    return {
      ...allMMHoursWithoutCommonHours,
      ...newMainMenuHours
    };
  }

  // Only closure dates changed, send the existing allMMHours
  return allMMHoursWithoutCommonHours;
};

/**
 * Calculate the next state of operating hours by combining recurring and special hours
 */
export const calculateNextOperatingHoursForMutation = ({
  nextAllMMHours,
  venueMMDates,
  currentActiveClosures,
  otherSpecialHours
}: {
  nextAllMMHours: AllMenuHours;
  venueMMDates: ClosedDates[];
  currentActiveClosures: OperatingHours[];
  otherSpecialHours: OperatingHours[];
}): OperatingHoursInput[] => {
  const newAndUpdatedRecurringHours = transformToDoshiiHours(nextAllMMHours);

  // All Newly created special hours need to be transformed to Doshii format from MM format
  const newSpecialHours = transformToDoshiiSpecialHours(venueMMDates);

  // TODO: later use correctDoshiiSpecialHours: See correctDoshiiSpecialHours function for more details
  // const updatedSavedClosures = correctDoshiiSpecialHours(originalActiveClosures, currentActiveClosures)
  const updatedSpecialHours = sanitizeDoshiiSpecialHoursForInput(currentActiveClosures);

  const passThroughUnhandledSpecialHours = sanitizeDoshiiSpecialHoursForInput(otherSpecialHours);

  return [
    ...newAndUpdatedRecurringHours,
    ...newSpecialHours,
    ...updatedSpecialHours,
    ...passThroughUnhandledSpecialHours
  ];
};

type UseOperatingHoursHookProps = {
  currentVenue?: Venue;
  appId?: string;
  doshiiMenu?: string;
};

export type UseOperatingHoursReturnType = {
  menuMMHours: MenuHoursType | null;
  setMenuMMHours: (setCurrentMenuMMHours: MenuHoursType) => void;
  todayMMHours: DayOfWeekType | null;
  submissionStatus: SubmissionStatus;
  isOperatingHoursSupported: boolean;
  venueMMDates: ClosedDates[];
  currentActiveClosures: OperatingHours[];
  setVenueMMDates: (dates: ClosedDates[]) => void;
  setCurrentActiveClosures: (hours: OperatingHours[]) => void;
  hasUnsavedChanges: boolean;
  clearAllUnsavedChanges: () => void;
  todayClosures: OperatingHours | null;
  commonHoursAcrossAllMenus: MenuHoursType | null;
  setCommonHoursAcrossAllMenus: (setCommonHoursAcrossAllMenus: MenuHoursType) => void;
  subscribedRecurringHoursSupportedApps: App[];
  hasCommonHoursAcrossAllMenusBeenApplied: boolean;
  submitHours: () => void;
};

/**
 * Custom hook to manage operating hours for a venue.
 */
export const useOperatingHours = ({
  currentVenue,
  appId,
  doshiiMenu
}: UseOperatingHoursHookProps): UseOperatingHoursReturnType => {
  const { allMMHours: allMMHoursInAppContext, setAllMMHours: setAllMMHoursInAppContext } = useAppContext();

  // Current menu hours state
  const [currentMenuMMHours, setCurrentMenuMMHours] = useState<MenuHoursType | null>(null);
  const [initialCurrentMenuMMHours, setInitialCurrentMenuMMHours] = useState<MenuHoursType | null>(null);
  const [todayMMHours, setTodayMMHours] = useState<DayOfWeekType | null>(null);

  // Common menu hours state
  const [commonHoursAcrossAllMenus, setCommonHoursAcrossAllMenus] = useState<MenuHoursType | null>(null);
  const [initialCommonHoursAcrossAllMenus, setInitialCommonMenuMMHours] = useState<MenuHoursType | null>(null);
  const [hasCommonHoursAcrossAllMenusBeenApplied, setHasCommonHoursAcrossAllMenusApplied] = useState<boolean>(false);

  // Special hours (closure state)
  const [currentActiveClosures, setCurrentActiveClosures] = useState<OperatingHours[]>([]);
  const [originalActiveClosures, setOriginalActiveClosures] = useState<OperatingHours[]>([]);
  const [otherSpecialHours, setOtherSpecialHours] = useState<OperatingHours[]>([]);
  const [todayClosures, setTodayClosures] = useState<OperatingHours | null>(null);
  const [venueMMDates, setVenueMMDates] = useState<ClosedDates[]>([]);

  // Form submission state
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>('IDLE');

  // Validation state
  const [activeMenulogMenus, setActiveMenulogMenus] = useState<string[] | undefined>();

  const subscribedRecurringHoursSupportedApps = useMemo(() => {
    return getSubscribedOperatingHoursSupportedApps(OPERATING_HOURS_WHITELISTED_APP_IDS, currentVenue);
  }, [currentVenue]);

  // Change detection
  const hasCommonHoursAcrossAllMenusChanged = !isEqual(initialCommonHoursAcrossAllMenus, commonHoursAcrossAllMenus);
  const hasDateChanged = !isEqual(originalActiveClosures, currentActiveClosures) || venueMMDates.length > 0;
  const hasSpecificMenuHourChanged = !isEqual(initialCurrentMenuMMHours, currentMenuMMHours);

  // Current day detection
  const currentDayOfWeekIndex = DateTime.now().setZone(currentVenue?.timezone || DEFAULT_TIMEZONE).weekday % 7;
  const currentDayOfWeek = DAYS_OF_WEEK[(currentDayOfWeekIndex + SHIFT_DAYS_LEFT) % DAYS_IN_WEEK];

  // Currently virtual brand is using the same operating hours, stripping the virtual prefix and hashId suffix is the best way to get the appId
  const currentAppId = appId?.startsWith('virtual') ? appId.split('.')[1] : appId;

  const currentMenuKey = currentAppId && doshiiMenu ? `${currentAppId}_${doshiiMenu}` : currentAppId || VENUE_KEY;

  // Check if operating hours are supported for the current venue/app
  const isOperatingHoursSupported = checkOperatingHoursSupported({
    currentAppId,
    currentVenue,
    operatingHoursWhitelistedAppIds: OPERATING_HOURS_WHITELISTED_APP_IDS,
    enableOperatingHoursForVirtualBrandVenues: ENABLE_OPERATING_HOURS_FOR_VIRTUAL_BRAND_VENUES,
    enableOperatingHoursForVenuesWithSkip: ENABLE_OPERATING_HOURS_FOR_VENUES_WITH_SKIP
  });

  // GraphQL mutation to update venue operating hours
  const [updateOperatingHoursMutation] = useMutation<
    UpdateOperatingHoursMutation,
    UpdateOperatingHoursMutationVariables
  >(UPDATE_OPERATING_HOURS_MUTATION, {
    update: (cache, { data }, { variables }) => {
      if (!variables) {
        throw new Error(`Expected mutation callback variables but got undefined`);
      }
      const updatedOperatingHours = data?.updateOperatingHours?.operatingHours;
      if (currentVenue && updatedOperatingHours) {
        const currentVenueId = cache.identify(currentVenue);
        cache.updateFragment(
          {
            fragmentName: 'VenueFragment',
            id: currentVenueId,
            fragment: VENUE_FRAGMENT
          },
          (data) => {
            return { ...data, operatingHours: updatedOperatingHours };
          }
        );
      }
    }
  });

  /**
   * Initialise operating hours data on component mount or when key dependencies(app, doshiiMenu, or day) change
   * This useEffect:
   * 1. Processes venue operating hours into two categories: recurring and special hours
   * 2. For recurring hours:
   *    - Loads menu manager format hours from context or transforms doshii format hours from currentVenue.operatingHours
   *    - Identifies potential default hours to apply across multiple apps' main menus
   *    - Ensures each required menu has properly initialised starting hours, including default hours if needed
   * 3. For special hours:
   *    - Separates active closures from other special hours
   *    - Identifies any closures affecting the current day
   */
  useEffect(() => {
    if (!currentVenue || !isOperatingHoursSupported) return;

    const operatingHours = currentVenue.operatingHours || [];
    const [specialHours, recurringHours] = splitOperatingHoursToSpecialAndRecurring(operatingHours);

    const processRecurringHours = (recurringHours: OperatingHours[]): void => {
      // Use recurringHours from context if available to avoid reprocessing

      /**
       * Structure of the `allMMHours` object:
       *
       * {
       *   commonHours: {
       *     mon: { day: 'mon', hours: { status: 'open', time: [{ from: '10:00', to: '14:00' }] } },
       *    ...
       *    sun: { day: 'sun', hours: { status: 'open', time: [{ from: '10:00', to: '14:00' }] } }
       *   },
       *   [appId]: {
       *     mon: { day: 'mon', hours: { status: 'open', time: [{ from: '10:00', to: '14:00' }, { from: '15:00', to: '20:00' }] } },
       *     ...
       *     sun: { day: 'sun', hours: { status: 'open', time: [{ from: '10:00', to: '14:00' }] } }
       *   },
       *   venue: {
       *     mon: { day: 'mon', hours: { status: 'open', time: [{ from: '10:00', to: '14:00' }] } },
       *     ...
       *     sun: { day: 'sun', hours: { status: 'open', time: [{ from: '10:00', to: '14:00' }] } }
       *   },
       *   [appId]_[doshiiMenu]: {
       *     mon: { day: 'mon', hours: { status: 'open', time: [{ from: '10:00', to: '14:00' }] } },
       *     ...
       *     sun: { day: 'sun', hours: { status: 'open', time: [{ from: '10:00', to: '14:00' }] } }
       *   }
       * }
       *
       * Notes:
       * - The `commonHours` key is used to store the common hours across all menus. Its either undefined when not computed, null when not applicable, or an object with the same structure as the other keys.
       * - Each key represents either a specific app hours, the venue hours itself(hours from pos/google-api), or timed menu hours for an app.
       * - Each day (mon–sun) contains the day's label and its corresponding open hours.
       * - Hours include a `status` ("open" or "closed") and an array of time ranges (`from` and `to`) in 24 hour string format("HH:mm").
       */
      const { commonHours: commonMMHoursFromContext, ...otherMMHoursFromContext } = cloneDeep(
        allMMHoursInAppContext ?? {}
      );

      const allMMHours = Object.keys(otherMMHoursFromContext).length
        ? otherMMHoursFromContext
        : transformToMMHours(recurringHours);

      let commonHours: MenuHoursType | null = null;
      if (subscribedRecurringHoursSupportedApps.length > 0) {
        // Compute commonHours only if not previously computed (i.e, it's `undefined` in AppContext)
        commonHours =
          commonMMHoursFromContext === undefined
            ? getCommonHoursAcrossAllMenus(currentVenue, allMMHours)
            : commonMMHoursFromContext;

        setCommonHoursAcrossAllMenus(commonHours);
        setInitialCommonMenuMMHours(commonHours);

        // We need to ensure that each menu hours are always initialised correctly
        let allMMHoursWithInitialisedHours = allMMHours;
        let isCommonHoursAppliedDuringInitialisation = false;

        // If currentAppId is present, we are editing a specific main menu or a specific timed menu. Only initialise hours for that specific menu if it doesn't exist
        if (currentAppId) {
          const menuKey = doshiiMenu ? `${currentAppId}_${doshiiMenu}` : currentAppId;
          // Only initialise if hours don't exist for this menu
          if (!allMMHoursWithInitialisedHours[menuKey]) {
            const { initialisedMenuHours, isCommonHoursApplied } = initialiseMenuHours(
              commonHours,
              allMMHoursWithInitialisedHours,
              currentAppId,
              doshiiMenu
            );
            allMMHoursWithInitialisedHours = initialisedMenuHours;
            isCommonHoursAppliedDuringInitialisation = isCommonHoursApplied;
          }
        } else {
          // If currentAppId is not present, we only need to initialise the main menu hours.
          // Timed menus are initialised only when both currentAppId and doshiiMenu are present—which happens when editing a specific menu from the menu page.
          subscribedRecurringHoursSupportedApps.forEach(({ id: appId }) => {
            // Only initialise if hours don't exist for this menu
            if (!allMMHoursWithInitialisedHours[appId]) {
              const { initialisedMenuHours, isCommonHoursApplied } = initialiseMenuHours(
                commonHours,
                allMMHoursWithInitialisedHours,
                appId
              );
              allMMHoursWithInitialisedHours = initialisedMenuHours;
              isCommonHoursAppliedDuringInitialisation =
                isCommonHoursAppliedDuringInitialisation || isCommonHoursApplied;
            }
          });
        }

        // If common hours are applied to any of the menu set hasCommonHoursAcrossAllMenusApplied to true
        if (isCommonHoursAppliedDuringInitialisation) {
          setHasCommonHoursAcrossAllMenusApplied(true);
        }

        // Set current menu hours
        setTodayMMHours(allMMHoursWithInitialisedHours?.[currentMenuKey]?.[currentDayOfWeek]);
        setCurrentMenuMMHours(allMMHoursWithInitialisedHours[currentMenuKey]);
        setInitialCurrentMenuMMHours(allMMHoursWithInitialisedHours[currentMenuKey]);
      }

      // Set all menu hours in app context if they have changed
      const allMMHoursWithCommonHours = { ...allMMHours, ...(commonHours && { commonHours }) };
      if (!isEqual(allMMHoursInAppContext, allMMHoursWithCommonHours)) {
        setAllMMHoursInAppContext(allMMHoursWithCommonHours);
      }
    };

    const processSpecialHours = (specialHours: OperatingHours[]): void => {
      const [activeClosures, otherSpecialHours] = splitSpecialHoursToActiveClosuresAndOthers(
        specialHours,
        currentVenue.timezone
      );

      // Process closures for today
      if (activeClosures.length) {
        const closureForToday = getClosuresForToday(activeClosures, currentVenue.timezone);
        const shouldSetTodayClosures =
          !currentAppId || CLOSED_DATES_WHITELISTED_APP_IDS.includes(parseInt(currentAppId, 10));

        if (shouldSetTodayClosures) {
          setTodayClosures(closureForToday);
        }
      }

      // Set all closures
      setCurrentActiveClosures(activeClosures);
      setOriginalActiveClosures(activeClosures);
      setOtherSpecialHours(otherSpecialHours);
    };

    processRecurringHours(recurringHours);

    processSpecialHours(specialHours);

    // Intentionally limiting dependencies to rerun this effect only when menu context or hours data changes.
    // Including state setters and other values would cause unnecessary re-initialisation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAppId, doshiiMenu, currentMenuKey, currentDayOfWeek, allMMHoursInAppContext]);

  /**
   * Handle operating hours submission to the backend
   *
   * This function:
   * 1. Processes only when submitOperatingHours becomes true
   * 2. Checks whether any changes need to be submitted:
   *    - Changes to specific menu hours
   *    - Changes to default hours that apply across apps
   *    - Changes to venue closures or special dates
   * 3. Transforms hours data into the format expected by the backend:
   *    - Converts menu manager specific hours to Doshii recurring hours format
   *    - Processes special closure dates into Doshii special hours format
   *    - Preserves any other special hours
   * 4. Submits all hour changes in a single mutation
   * 5. Updates app context with new hours on success
   *
   * If no changes are detected, the submission is marked successful without an API call
   */
  const submitOperatingHoursUpdate = useCallback(async (): Promise<void> => {
    if (!currentVenue || !allMMHoursInAppContext) {
      return;
    }

    const hasChangesToSubmit =
      hasCommonHoursAcrossAllMenusChanged ||
      hasCommonHoursAcrossAllMenusBeenApplied ||
      hasDateChanged ||
      hasSpecificMenuHourChanged;

    // Avoid unnecessary API calls when there are no changes
    if (!hasChangesToSubmit) {
      setSubmissionStatus('SUCCESS');
      return;
    }

    const nextAllMMHours = calculateNextAllMMHours({
      allMMHoursInAppContext,
      hasSpecificMenuHourChanged,
      currentMenuMMHours,
      currentMenuKey,
      hasCommonHoursAcrossAllMenusChanged,
      hasCommonHoursAcrossAllMenusBeenApplied,
      commonHoursAcrossAllMenus,
      subscribedRecurringHoursSupportedApps
    });

    // Calculate the full operating hours for mutation
    const operatingHours = calculateNextOperatingHoursForMutation({
      nextAllMMHours,
      venueMMDates,
      currentActiveClosures,
      otherSpecialHours
    });

    try {
      setSubmissionStatus('PENDING');

      await updateOperatingHoursMutation({
        variables: {
          locationId: currentVenue?.hashedId || '',
          operatingHours
        }
      });

      setAllMMHoursInAppContext(nextAllMMHours);
      setSubmissionStatus('SUCCESS');
    } catch (error) {
      setSubmissionStatus('FAILURE');
    }
  }, [
    currentVenue,
    allMMHoursInAppContext,
    hasCommonHoursAcrossAllMenusChanged,
    hasCommonHoursAcrossAllMenusBeenApplied,
    hasDateChanged,
    hasSpecificMenuHourChanged,
    currentMenuMMHours,
    currentMenuKey,
    commonHoursAcrossAllMenus,
    subscribedRecurringHoursSupportedApps,
    venueMMDates,
    currentActiveClosures,
    otherSpecialHours,
    updateOperatingHoursMutation,
    setAllMMHoursInAppContext
  ]);

  const validateAndSetCurrentMenuMMHours = useCallback(
    (inputMenuHours: MenuHoursType): void => {
      let hoursWithValidation = findInvalidHours(inputMenuHours);

      if (currentAppId && Number(currentAppId) === MENULOG_ID) {
        const currentActiveMenus = activeMenulogMenus ?? getActiveMenusForApp(currentAppId, currentVenue);

        // Update state for future calls
        if (activeMenulogMenus === undefined) {
          setActiveMenulogMenus(currentActiveMenus);
        }

        hoursWithValidation = findOverlappingTimes(
          currentMenuKey,
          hoursWithValidation,
          allMMHoursInAppContext,
          currentActiveMenus
        );
      }

      setCurrentMenuMMHours(hoursWithValidation);
    },
    [currentMenuKey, allMMHoursInAppContext, currentAppId, currentVenue, activeMenulogMenus]
  );

  const validateAndSetCommonHoursAcrossAllMenus = useCallback((inputCommonHours: MenuHoursType): void => {
    const hoursWithValidation = findInvalidHours(inputCommonHours);
    setCommonHoursAcrossAllMenus(hoursWithValidation);
  }, []);

  const clearAllUnsavedChanges = useCallback((): void => {
    // Reset the special hours with dates with empty array
    setVenueMMDates([]);
    // Set the activeClosure to original value
    setCurrentActiveClosures(originalActiveClosures);
    // TODO: later refactor to use clearAllUnsavedChanges for recurring hours as well
  }, [originalActiveClosures]);

  return {
    // Menu hours configuration
    menuMMHours: currentMenuMMHours,
    setMenuMMHours: validateAndSetCurrentMenuMMHours,
    todayMMHours,

    // Common hours across all menus configuration
    commonHoursAcrossAllMenus,
    setCommonHoursAcrossAllMenus: validateAndSetCommonHoursAcrossAllMenus,
    hasCommonHoursAcrossAllMenusBeenApplied, // TODO: use this to show notification when common hours is applied during initialisation and need saving

    // Closures and special dates
    todayClosures,
    currentActiveClosures,
    setCurrentActiveClosures,
    venueMMDates,
    setVenueMMDates,

    // Form state
    submissionStatus,
    hasUnsavedChanges: hasCommonHoursAcrossAllMenusChanged || hasDateChanged || hasSpecificMenuHourChanged,
    clearAllUnsavedChanges,
    submitHours: submitOperatingHoursUpdate,

    // Support information
    isOperatingHoursSupported,
    subscribedRecurringHoursSupportedApps
  };
};
